import { apiClient } from './apiClient';

export type ExportCell = string | number | boolean | null | undefined;
export type ExportRow = Record<string, ExportCell>;

export type ExportStatus = 'pending' | 'processing' | 'ready' | 'failed';

export interface ExportHandle {
  exportId: number;
  downloadToken: string;
  status: ExportStatus;
}

export interface ExportStatusResponse {
  exportId: number;
  status: ExportStatus;
  rowCount: number | null;
  error: string | null;
  createdAt: string;
}

export interface ExportDownloadResponse {
  url: string;
  rowCount: number;
  filename: string;
}

/**
 * Fetch the canonical column list for an entity type. Memoized per entity
 * type since the headers are static for the life of the page.
 */
const columnsCache = new Map<string, string[]>();

export async function getExportColumns(entityType: string): Promise<string[]> {
  const cached = columnsCache.get(entityType);
  if (cached) return cached;

  const response = await apiClient.get(`/list-exports/columns/${entityType}`);
  const columns = (response.data.data?.columns ?? []) as string[];
  columnsCache.set(entityType, columns);
  return columns;
}

/**
 * Request an async export. Returns immediately with an export handle
 * that can be used for polling and download.
 */
export async function requestExport(
  entityType: string,
  filters: Record<string, unknown> = {},
  columns?: string[],
): Promise<ExportHandle> {
  const body: Record<string, unknown> = { entityType, filters };
  if (columns && columns.length) body.columns = columns;
  const response = await apiClient.post('/list-exports', body);
  return response.data.data as ExportHandle;
}

/**
 * Check the status of a running export.
 */
export async function getExportStatus(
  exportId: number,
  downloadToken?: string,
): Promise<ExportStatusResponse> {
  const params = downloadToken ? { downloadToken } : {};
  const response = await apiClient.get(`/list-exports/${exportId}/status`, { params });
  return response.data.data as ExportStatusResponse;
}

/**
 * Get the presigned download URL for a completed export.
 */
export async function getExportDownload(
  exportId: number,
  downloadToken?: string,
): Promise<ExportDownloadResponse> {
  const params = downloadToken ? { downloadToken } : {};
  const response = await apiClient.get(`/list-exports/${exportId}/download`, { params });
  return response.data.data as ExportDownloadResponse;
}

/**
 * Trigger the browser download from a presigned URL.
 */
export function triggerDownload(url: string, filename: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * How long to keep polling an export the server still reports as 'pending'.
 *
 * The status endpoint marks an export failed, with a message saying what to do,
 * once it has been pending for StalePendingExport::THRESHOLD_MINUTES (10) on the
 * server. This waits a minute past that so the poll that brings that message
 * back still happens. list-export.service.polling.test.ts pins it above the
 * server value.
 */
export const EXPORT_PENDING_TIMEOUT_MS = 11 * 60 * 1000;

/** How long to keep polling once the export has been seen 'processing'. */
export const EXPORT_PROCESSING_TIMEOUT_MS = 180 * 1000;

/**
 * Poll an export until it reaches a terminal state (ready or failed).
 * Calls `onStatusChange` on each poll so the UI can update.
 * Returns the final status response.
 *
 * Bounded so a row the backend never finishes (e.g. the worker was hard-killed
 * and the row is stranded at 'processing') throws instead of looping forever.
 * Two limits apply:
 * - while 'pending', `pendingTimeoutMs` from the start — long enough for the
 *   server to turn an export that never started into a failure with a message;
 * - once 'processing' is first seen, `timeoutMs` from that poll. Measuring from
 *   there means an export picked up late still gets the full build time, and
 *   one picked up at once gets the same cap as before.
 */
export async function pollUntilReady(
  exportId: number,
  downloadToken: string,
  onStatusChange?: (status: ExportStatusResponse) => void,
  intervalMs = 2500,
  timeoutMs = EXPORT_PROCESSING_TIMEOUT_MS,
  pendingTimeoutMs = EXPORT_PENDING_TIMEOUT_MS,
): Promise<ExportStatusResponse> {
  const startedAt = Date.now();
  let processingDeadline: number | null = null;
  while (true) {
    const status = await getExportStatus(exportId, downloadToken);
    onStatusChange?.(status);

    if (status.status === 'ready' || status.status === 'failed') {
      return status;
    }

    if (status.status !== 'pending' && processingDeadline === null) {
      processingDeadline = Date.now() + timeoutMs;
    }
    const deadline = processingDeadline ?? startedAt + pendingTimeoutMs;

    if (Date.now() >= deadline) {
      throw new Error('Export timed out — it is taking longer than expected. Please try again, or narrow the date range / filters.');
    }

    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
}

/**
 * Full async export flow: request -> poll -> download.
 * Returns the row count on success, throws on failure.
 */
export async function runAsyncExport(
  entityType: string,
  filters: Record<string, unknown>,
  onStatusChange?: (status: ExportStatusResponse) => void,
  columns?: string[],
): Promise<number> {
  const handle = await requestExport(entityType, filters, columns);

  const finalStatus = await pollUntilReady(
    handle.exportId,
    handle.downloadToken,
    onStatusChange,
  );

  if (finalStatus.status === 'failed') {
    throw new Error(finalStatus.error ?? 'Export failed');
  }

  const download = await getExportDownload(handle.exportId, handle.downloadToken);
  triggerDownload(download.url, download.filename);

  return download.rowCount;
}

/**
 * @deprecated Use runAsyncExport instead. Kept for any code that hasn't migrated yet.
 */
export async function downloadSpreadsheetExport(
  filenamePrefix: string,
  rows: ExportRow[],
): Promise<void> {
  const response = await apiClient.post(
    '/reports/export',
    { data: rows, filename: `${filenamePrefix}-${new Date().toISOString().slice(0, 10)}` },
    { responseType: 'blob' },
  );

  const blob = new Blob(
    [response.data as BlobPart],
    { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
  );
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filenamePrefix}-${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
